import React from "react";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
} from "@mui/material";
import * as Yup from "yup";
import { PlacementQuestion } from "../../types/PlacementQuestion";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export interface PlacementQuestionProps {
  selectedPlacementQuestion?: PlacementQuestion;
  onSubmit: (values: PlacementQuestion) => Promise<void>;
}

export const PlacementQuestionForm: React.FC<PlacementQuestionProps> = ({
  selectedPlacementQuestion,
  onSubmit,
}) => {
  const formik = useFormik({
    initialValues: {
      _id: undefined,
      difficulty: "Easy",
      type: "Multiple Choice",
      question: "",
      choices: [],
      answer: "",
      isActive: false,
      ...selectedPlacementQuestion,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      difficulty: Yup.string()
        .oneOf(["Easy", "Hard"], "Invalid Difficulty")
        .required(),
      type: Yup.string()
        .oneOf(["Fill in the blank", "Multiple Choice"], "Invalid Type")
        .required(),
      question: Yup.string().required("Question is required"),
      choices: Yup.array().when("type", (type, schema) => {
        if (typeof type === "string" && type === "Multiple Choice") {
          return schema.of(Yup.string().required()).required();
        } else {
          return schema.notRequired();
        }
      }),

      answer: Yup.string().required("Answer is required"),
      isActive: Yup.boolean(),
    }),

    onSubmit: async (values: PlacementQuestion) => {
      await onSubmit(values);
      console.log(values);
      // formik.resetForm({ values });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        {/* Difficulty Field */}
        <Grid item xs={12}>
          <TextField
            select
            size="small"
            fullWidth
            autoComplete="off"
            id="difficulty"
            name="difficulty"
            label="Difficulty"
            value={formik.values.difficulty}
            onChange={formik.handleChange}
            error={
              formik.touched.difficulty && Boolean(formik.errors.difficulty)
            }
            helperText={formik.touched.difficulty && formik.errors.difficulty}
            SelectProps={{ native: true }}
          >
            <option value="Easy">Easy</option>
            <option value="Hard">Hard</option>
          </TextField>
        </Grid>

        {/* Type Field */}
        <Grid item xs={12}>
          <TextField
            select
            size="small"
            fullWidth
            autoComplete="off"
            id="type"
            name="type"
            label="Type"
            value={formik.values.type}
            onChange={formik.handleChange}
            error={formik.touched.type && Boolean(formik.errors.type)}
            helperText={formik.touched.type && formik.errors.type}
            SelectProps={{ native: true }}
          >
            <option value="Fill in the blank">Fill in the Blank</option>
            <option value="Multiple Choice">Multiple Choice</option>
          </TextField>
        </Grid>

        {/* Question Field */}
        <Grid item xs={12} style={{ marginBottom: "3rem" }}>
          <ReactQuill
            value={formik.values.question}
            onChange={(value) => formik.setFieldValue("question", value)}
            style={{ height: "150px" }}
          />
          {formik.touched.question && Boolean(formik.errors.question) && (
            <div style={{ color: "red" }}>{formik.errors.question}</div>
          )}
        </Grid>

        {/* Choices Field */}
        {formik.values.type === "Multiple Choice" && (
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="choices"
              name="choices"
              label="Choices (comma-separated)"
              value={formik.values.choices.join(", ")}
              onChange={(e) => {
                formik.setFieldValue(
                  "choices",
                  e.target.value.split(", ").map((choice) => choice.trim())
                );
              }}
              error={formik.touched.choices && Boolean(formik.errors.choices)}
              helperText={formik.touched.choices && formik.errors.choices}
            />
          </Grid>
        )}

        {/* Answer Field */}
        <Grid item xs={12}>
          {formik.values.type === "Multiple Choice" ? (
            <TextField
              select
              size="small"
              fullWidth
              autoComplete="off"
              id="answer"
              name="answer"
              label="Answer"
              value={formik.values.answer}
              onChange={formik.handleChange}
              error={formik.touched.answer && Boolean(formik.errors.answer)}
              helperText={formik.touched.answer && formik.errors.answer}
              SelectProps={{ native: true }}
            >
              <option aria-label="None" value="" />
              {formik.values.choices.map((choice, index) => (
                <option key={index} value={choice}>
                  {choice}
                </option>
              ))}
            </TextField>
          ) : (
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="answer"
              name="answer"
              label="Answer"
              value={formik.values.answer}
              onChange={formik.handleChange}
              error={formik.touched.answer && Boolean(formik.errors.answer)}
              helperText={formik.touched.answer && formik.errors.answer}
            />
          )}
        </Grid>

        {/* isActive Field */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={formik.values.isActive}
                onChange={(event) => {
                  formik.setFieldValue("isActive", event.target.checked);
                }}
              />
            }
            label={formik.values.isActive ? "Active" : "Disabled"}
            labelPlacement="start"
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={!formik.dirty}
          >
            {formik.values._id ? "Update" : "Add New"} Placement Question
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
